import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from '../../../common/utils/withRouter'
import _ from 'lodash'
import classNames from 'classnames'
import BigBreadcrumbs from '../../../components/layout/navigation/components/BigBreadcrumbs'
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid'
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget'
import { createStatusBadgeString } from '../../../common/components/diamond/StatusBadge'
import {
  fetchDiamonds,
  clearDiamonds,
  clearSelectedDiamond
} from '../../../common/actions/diamond'
import { getRetailer, getBestCollection, addBestCollection, removeBestCollection } from '../../../common/actions/retailer'
import { isStandardRetailer, } from '../../../common/utils/retailer'
import {
  statusCodes,
  jewelryTypes,
  settings
} from '../../../common/utils/diamond'
import imageUrl from '../../../common/utils/imageUrl'
import { LIST_PAGE_LENGTH } from '../../../common/constants'
import UpgradeAccountButton from '../../../common/components/common/UpgradeAccountButton'
import ClearFormFieldButton from '../../../common/components/common/ClearFormFieldButton'
import EmptyTableDiamonds from '../../../common/components/diamond/EmptyTableDiamonds'
import { ToastContainer, toast } from 'react-toastify';
import styles from './List.module.css'
import DataTable from '../../../components/tables/Datatable'
import defaultDiamondImage from '../../../assets/images/image-coming-soon.jpg'

const collectionToMap = (res) => {
  const map = {}
  if (res && Array.isArray(res.data)) {
    res.data.forEach(entity => {
      const productId = typeof entity.get === 'function' ? entity.get('productId') : entity.productId
      if (productId) {
        map[productId] = { isInCollection: true }
      }
    })
  }
  return map
}

// Gemstones and jewelry come back with cut/clarity/color as null. A renderer
// that throws aborts the whole DataTables draw, and the fetch callback's catch
// then redraws with no rows — so one null blanked the entire page.
const isNA = value =>
  value == null || value === '' || value === 'not-applicable'
const renderCarat = carat => (isNA(carat) || carat <= 0 ? 'N/A' : carat)
const renderTitle = title =>
  isNA(title) ? 'N/A' : String(title).split('-').map(_.capitalize).join(' ')
const renderClarity = clarity =>
  isNA(clarity) ? 'N/A' : String(clarity).replace(/-/g, ' ').toUpperCase()
const renderUpper = value => (isNA(value) ? 'N/A' : _.toUpper(value))
const renderColor = value =>
  isNA(value) ? 'N/A' : renderUpper(String(value).replace(/-/g, ' '))

// The image lives in a fixed-size box that starts in the isLoading state; the
// img's own onload clears it. Reserving the space keeps the row height stable
// while the file is in flight, so the list no longer steps up and down as a
// page's images arrive one at a time. onerror swaps in the placeholder and lets
// its load event clear isLoading in turn.
const renderImage = img => `
  <div class="${styles.imageColumn}">
    <div class="${styles.expandButton}">+</div>
    <div class="${styles.imageBox} ${styles.isLoading}">
      <img
        src="${imageUrl('inventory', img)}"
        onload="this.parentNode.classList.remove('${styles.isLoading}');"
        onerror="this.onerror=null; this.src='${defaultDiamondImage}';"
      />
    </div>
  </div>
`

// Seller-created items leave inventory.internal_id NULL — the seller form's
// Internal ID input is read-only and nothing generates a value — so fall back to
// the product uuid, the same way the model serializer (internal_id ?? uuid) and
// the other lists do. Without it the column printed the literal text "null".
const renderInternalId = (internalId, type, row) => `
  <div class="${styles.internalIdColumn}">
    <div>${internalId || (row && (row.uuid || row.diamondId)) || 'N/A'}</div>
  </div>
`

const isDifferentPage = (currentProps, nextProps) =>
  currentProps.location.query.page !== nextProps.location.query.page

const settingFilterNullItem = {
  value: null,
  text: 'All Settings/Jewelry'
}

const settingFilterItems = [
  {
    value: 'loose',
    text: 'Loose Diamonds'
  },
  {
    value: 'gemstone',
    text: 'Gemstones'
  },
  {
    value: 'mounted',
    text: 'Mounted Jewelry'
  },
  {
    value: 'non-diamond-jewelry',
    text: 'Non-Diamond Jewelry'
  },
  {
    value: 'craft-jewelry',
    text: 'Craft Jewelry'
  },
  {
    value: 'service',
    text: 'Service'
  }
].concat(
  Object.keys(jewelryTypes).map(value => ({
    value,
    text: jewelryTypes[value]
  }))
)

class DesignsCollections extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedSetting: settingFilterNullItem,
      collection: null,
      loading: false,
      error: null
    }
  }
  componentDidMount() {
    const {
      isAdminUser,
      retailer,
      currentUser,
      getRetailer,
      clearSelectedDiamond,
      collection
    } = this.props
    if (!isAdminUser && !retailer && currentUser) {
      const id = typeof currentUser.get === 'function' ? currentUser.get('id') : currentUser.id
      getRetailer(id)
    }


    clearSelectedDiamond()
    collection().then((res) => {
      this.setState({ loading: false })
      if (res && res.response) {
        // API returned an error (e.g. 403 Forbidden)
        const status = res.response.status
        const message =
          (res.response.data && res.response.data.message) ||
          `Request failed with status ${status}`
        this.setState({ collection: {}, error: message })
      } else {
        this.setState({ collection: collectionToMap(res) })
      }
    }).catch(() => this.setState({ loading: false, collection: {}, error: 'Failed to load collection data.' }))
    this.handleSpaClick = (e) => {
      const anchor = e.target.closest('a[data-spa-href]')
      if (!anchor) return
      e.preventDefault()
      const href = anchor.getAttribute('data-spa-href')
      this.props.router.push(href)
    }
    this.containerNode && this.containerNode.addEventListener('click', this.handleSpaClick)
  }
  componentWillUnmount() {
    this.containerNode && this.containerNode.removeEventListener('click', this.handleSpaClick)
  }
  componentDidUpdate(prevProps) {
    const { router, location, diamonds, diamondInProgress } = this.props
    if (
      !diamonds ||
      isDifferentPage(prevProps, this.props) ||
      diamondInProgress
    ) {
      return
    }
    const nextPage = String(location.query.page || '')
    const page = diamonds.getMeta('pageNumber')
    if (nextPage !== String(page)) {
      const newLocation = {
        pathname: location.pathname,
        query: Object.assign({}, location.query, { page })
      }
      if (location.query.page) {
        router.push(newLocation)
      } else {
        router.replace(newLocation)
      }
    }
  }
  render() {
    const self = this
    const {
      location,
      isAdminUser,
      retailer,
      diamonds,
      diamondInProgress,
      fetchDiamonds,
      addCollection,
      removeCollection,
      collection,
      bestDesign

    } = this.props

    if (!isAdminUser && !retailer && !location.query.page) {
      return null
    }
    let page = parseInt(location.query.page)
    page = _.isInteger(page) ? Math.max(page, 1) : 1
    const displayStart = (page - 1) * LIST_PAGE_LENGTH

    const columns = [
      { data: 'firstImagePath', render: renderImage },
      { data: 'retailerName', isHidden: !isAdminUser, className: 'Retailer' },
      {
        data: null,
        render: (data, type, row) => {
          // diamondId is set by the Laravel inventory API (data.attributes.diamond_id → camelCase).
          // objectID is set by Algolia search results.
          // Always prefer diamondId (the inventory UUID) as the canonical product_id.
          const productId = data.diamondId || data.objectID;

          const checked = this.state.collection != null && this.state.collection[productId]
            ? this.state.collection[productId]
            : { isInCollection: false };

          const isDisabled = row.statusCode === 6;
          const checkedAttribute = checked.isInCollection ? 'checked' : '';

          return `
            <div class="form-check form-switch">
              <input
                id="check-toggle-${row.id}"
                class="form-check-input toggle-feature"
                type="checkbox"
                role="switch"
                data-id="${productId}"
                data-is-disabled="${isDisabled}"
                ${checkedAttribute}

              >
              <label for="check-toggle-${row.id}"></label>
            </div>
          `;
        }


      },
      { data: 'internalId', render: renderInternalId },
      {
        data: 'statusCode',
        render: createStatusBadgeString
      },
      { data: 'price' },
      { data: 'setting', render: this.renderSetting },
      { data: 'shape', render: renderTitle },
      { data: 'carat', render: renderCarat },
      { data: 'cut', render: renderTitle },
      { data: 'clarity', render: renderClarity },
      { data: 'certificate', render: renderUpper },
      { data: 'color', render: renderColor },

      // { data: null, render: this.renderEditButtons }
    ].filter(col => !col.isHidden)


    return (
      <div>
        <ToastContainer />
        <div id="content" className={styles.listWrapper} ref={node => { this.containerNode = node }}>
          <div className="row">
            <BigBreadcrumbs
              items={['Designs and Collections']}
              icon="table"
              className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
            />
            {this.renderActionButtons()}
          </div>
          {diamonds &&
            diamonds.size() === 0 &&
            !diamonds.getMeta('query') &&
            !diamondInProgress
            ? <EmptyTableDiamonds />
            : null}
          <WidgetGrid>
            <div className="row">
              <article className="col-sm-12">
                <JarvisWidget
                  editbutton={false}
                  togglebutton={false}
                  fullscreenbutton={false}
                  deletebutton={false}
                  color="darken"
                >
                  <header>
                    <span className="widget-icon">
                      <i className="fa fa-list" />
                    </span>
                    <h2>Items</h2>
                  </header>
                  <div>
                    {this.state.error ? (
                      <div className="widget-body" style={{ padding: '20px' }}>
                        <div className="alert alert-danger" role="alert">
                          <strong>Error:</strong> {this.state.error}
                        </div>
                      </div>
                    ) : this.state.collection != null ? <div className="widget-body no-padding">
                      <DataTable
                        options={{
                          processing: true,
                          serverSide: true,
                          ajax: (query, callback) => {
                            fetchDiamonds(
                              this.state.selectedSetting,
                              query,
                              callback
                            )
                          },
                          lengthChange: false,
                          pageLength: LIST_PAGE_LENGTH,
                          displayStart,
                          search: {
                            search: location.query.query
                          },
                          columns,
                          drawCallback: function () {
                            // Attach event listener to the parent container
                            $('.form-check').off('click').on('click', '.toggle-feature', function (e) {
                              const isDisabled = $(this).data('is-disabled');

                              if (isDisabled) {
                e.preventDefault(); // Prevent checkbox interaction
                                //alert('This action is not allowed for the deleted items.');
                                toast(
                                  ({ closeToast }) => (
                                    <div>
                                      <p>This action is not allowed for the deleted items.</p>

                                    </div>
                                  ),
                                  {
                                    position: toast.POSITION.TOP_CENTER,
                                    autoClose: 2000, // Keeps the toast open until user action
                                    closeOnClick: false, // Prevent clicking on toast to close
                                    draggable: false, // Disable dragging the toast
                                  }
                                );
                              } else {
                                const productId = $(this).data('id');
                                const isChecked = $(this).is(':checked');
                                const data = { product_id: productId };

                                if (isChecked) {
                                  addCollection(data).then((res) => {
                                    collection().then((res) => {
                                      self.setState({ collection: collectionToMap(res) });
                                    });
                                  });
                                } else {
                                  removeCollection(data).then((res) => {
                                    collection().then((res) => {
                                      self.setState({ collection: collectionToMap(res) });
                                    });
                                  });
                                }
                              }
                            });
                          }

                          ,
                          responsive: { details: { type: 'inline' } },
                        }}
                        searchPlaceholder={`Retailer`}
                        className={classNames(
                          'table table-striped table-bordered table-hover',
                          styles.table
                        )}
                        width="100%"
                        ref={node => {
                          this.cachedDatatable = node && node.cachedDatatable
                        }}
                      >
                        <thead>
                          <tr>
                            <th>Image</th>
                            {isAdminUser && <th>Retailer</th>}
                            <th style={{ minWidth: '100px' }}>Best Collection</th>
                            <th>Internal ID</th>

                            <th>Status</th>
                            <th>Price</th>
                            <th>Setting</th>
                            <th>Shape</th>
                            <th>Carat</th>
                            <th>Cut</th>
                            <th>Clarity</th>
                            <th>Certs</th>
                            <th>Color</th>
                            {/* <th
                            data-class="expand"
                            className={styles.actionsColumn}
                          >
                            Actions
                          </th> */}
                          </tr>
                        </thead>
                      </DataTable>
                      {this.renderSettingFilter()}
                    </div> : "Loading..."
                    }
                  </div>
                </JarvisWidget>
              </article>
              <div className={styles.sectionHelp}>
                <i className="fa fa-question-circle" />
                <div className={styles.sectionHelpBlurb}>
                  <span>
                    <a
                      href="https://intercom.help/gemsby/selling-on-gemsby"
                      target="_blank"
                    >
                      Gemsby Help Center
                    </a>
                  </span>
                  <a
                    href="https://intercom.help/gemsby/selling-on-gemsby/preparing-your-store/tips-on-preparing-your-store"
                    target="_blank"
                  >
                    <strong>Tips on Preparing Your Store</strong>
                  </a>
                </div>
              </div>
            </div>
          </WidgetGrid >
        </div >
      </div>

    )
  }
  renderActionButtons() {
    const { router, isAdminUser, retailer, diamonds } = this.props
    if (!diamonds) {
      return null
    }
    if (isAdminUser || isStandardRetailer(retailer)) {
      return (
        <div className={styles.actionButtons}>
          {/* {isAdminUser &&
            <button
              className={`btn btn-primary ${styles.actionButton}`}
              onClick={() => {
                router.push('/inventory/csvUpload')
              }}
            >
              Bulk Upload (CSV File)
            </button>}
          {this.renderAddButton()} */}
        </div>
      )
    }
    if (diamonds.data.length < 2) {
      return (
        <div className={styles.actionButtons}>
          {this.renderAddButton()}
        </div>
      )
    }
    return (
      <div className={styles.actionButtons}>
        <div className={styles.upgradeMessage}>
          You have reached the account limit, upgrade to{' '}
          <strong>Standard Account</strong> to upload more inventory.
        </div>
        <UpgradeAccountButton className={styles.actionButton} />
      </div>
    )
  }
  renderAddButton() {
    return (
      <button
        className={`btn btn-primary ${styles.actionButton}`}
        onClick={() => {
          this.props.router.push('/inventory/new')
        }}
      >
        Add an Item
      </button>
    )
  }
  renderSettingFilter() {
    return (
      <div className={styles.settingFilter}>
        <div className={classNames('input-group', styles.settingFilterField)}>
          <span className="input-group-addon input-sm">
            <i className="glyphicon glyphicon-filter" />
          </span>
          <div
            className={classNames(
              'form-control',
              'input-sm',
              styles.settingFilterDropdown
            )}
          >
            <button
              type="button"
              className="dropdown-toggle"
              data-toggle="dropdown"
            >
              <span>
                {this.state.selectedSetting.text}
              </span>
              <span className="caret" />
            </button>
            <ul className="dropdown-menu">
              {settingFilterItems.map(item =>
                <li key={item.value}>
                  <a href="#" onClick={this.onFilterSelect(item)}>
                    {item.text}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
        {this.state.selectedSetting.value &&
          <ClearFormFieldButton onClick={this.onFilterClear} />}
      </div>
    )
  }
  renderSetting = (setting, type, row) => {
    if (setting === 'mounted' || setting === 'non-diamond-jewelry') {
      const jewelryType = jewelryTypes[row.jewelryType]
      if (jewelryType) {
        return jewelryType
      }
    }
    return settings[setting] || setting
  }
  renderEditButtons = ({ id, statusCode }) => {
    const { location, diamonds } = this.props
    if (diamonds) {
      location.query.query = diamonds.getMeta('query')
    }
    const backUrl = encodeURIComponent(history.createPath(location))
    id = encodeURIComponent(id)
    const liveLink =
      statusCode === statusCodes.DELETED.value
        ? ''
        : ``
    return `<div>
      <a href="/product-review/${id}" data-spa-href="/product-review/${id}">
        <button class="btn btn-primary">
          View Product Reviews
        </button>
      </a>
      ${liveLink}
    </div>`
  }
  setSettingFilter(item) {
    this.setState({ selectedSetting: item })
    setTimeout(() => {
      this.cachedDatatable.search('').draw()
    })
  }
  onFilterSelect = item => {
    return event => {
      event.preventDefault()
      if (!this.cachedDatatable) {
        return
      }
      this.setSettingFilter(item)
    }
  }
  onFilterClear = event => {
    event.preventDefault()
    if (!this.cachedDatatable) {
      return
    }
    this.props.clearDiamonds()
    this.setSettingFilter(settingFilterNullItem)
  }
}

const mapStateToProps = ({ retailer, diamond, login }) => {
  return {
    retailer: retailer.get('selectedRetailer'),
    diamonds: diamond.get('diamonds'),
    diamondInProgress: diamond.get('inProgress'),
    isAdminUser: login.get('isAdminUser'),
    currentUser: login.get('currentUser'),
    bestDesign: retailer.get('bestCollection')
  }
}

const mapDispatchToProps = dispatch => {
  return {
    fetchDiamonds(selectedSetting, query, callback) {
      // The API takes the dropdown value unchanged and works out whether it
      // names a setting or a jewelry type. The Algolia-shaped split below is
      // left alone because the other diamond lists still rely on it.
      query.selectedFilter = selectedSetting.value
      query.setting = selectedSetting.value
      if (query.setting && query.setting !== 'loose') {
        query.setting = 'mounted'
        query.jewelryType = selectedSetting.value
      }
      dispatch(
        fetchDiamonds(query, diamonds => {
          setTimeout(() => {
            try {
              if (!diamonds) {
                callback({ recordsTotal: 0, recordsFiltered: 0, data: [] })
                return
              }
              const recordsTotal = diamonds.getMeta('recordsTotal') || 0
              callback({
                recordsTotal,
                recordsFiltered: recordsTotal,
                data: (diamonds.toJSON() || [])
                  .map(item =>
                    _.extend(item.attributes, {
                      id: item.id
                    })
                  )
                  .sort((a, b) => parseFloat(b.price) - parseFloat(a.price))
              })
            } catch (e) {
              console.error('DataTable fetch error:', e)
              callback({ recordsTotal: 0, recordsFiltered: 0, data: [] })
            }
          })
        }, { marketplaceVisibleOnly: true })
      )
    },
    getRetailer(retailerId) {
      dispatch(getRetailer(retailerId))
    },
    clearDiamonds() {
      dispatch(clearDiamonds())
    },
    clearSelectedDiamond() {
      dispatch(clearSelectedDiamond())
    },

    addCollection: (data) => dispatch(addBestCollection(data)),
    removeCollection: (data) => dispatch(removeBestCollection(data)),
    collection: () => dispatch(getBestCollection())
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DesignsCollections)
)
